// has to stay the very first import: it patches console.warn/error, and @adornis/base/logging.js
// freezes references to those two when it is evaluated (see console-recorder.ts)
import '../diagnostics/console-recorder.js';

import '@adornis/accounting/index.js';
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { QueryCache } from '@adornis/baseql/client/requestHandlers/queryCache.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/chat/baseql-index.js';
import '@adornis/chat-agent/baseql-index.js';
import '@adornis/chat-assist/baseql-index.js';
import '@adornis/dms/baseql-index.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import '@adornis/conversation/index.js';
import '@adornis/fonts/fontSrc/lato.js';
import '@adornis/fonts/fonts.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { currentUserReconnectGuard } from '@adornis/users/client/currentUserReconnectGuard.js';
import '@adornis/users/db/index.js';
import '@adornis/versioned-entity/api/get-last-editor.js';
import '@adornis/versioned-entity/api/get-patch-view-list.js';
import '@adornis/view-manager/db/index.js';
import '@martinus/address/index.js';
import '../accounting/index.js';
import '../admin-dashboard/index.js';
import '../austritt/index.js';
import '../billingPositions/index.js';
import '../bookings/index.js';
import '../db/index.js';

import { provideGlobalContext } from '@adornis/functional-lit/directive-provide-context.js';
import { translationContext } from '@adornis/internationalization/client/translation-context.js';
import { de as countryLanguageDe, registerCountryLocale } from '@adornis/internationalization/isoCountries.js';
import '../abwesenheiten/index.js';
import '../accounting-import/index.js';
import '../accounting-matching/index.js';
import '../accounting-resolution/index.js';
import '../employment-permits/index.js';
import { registerMartinusCache } from '../entity-cache/index.js';
import '../groups/index.js';
import '../history/index.js';
import { m } from '../localization/out/messages.js';
import { MicrosoftUserData } from '../microsoft-graph/entities/MicrosoftUserData.js';
import '../microsoft-graph/index.js';
import '../permissions/index.js';
import '../quittungen/index.js';
import '../salary-table/index.js';
import '../sepa/index.js';
import '../sus-entry-form/index.js';
import '../ticket/index.js';
import '../user-emails/index.js';
import '../users/index.js';
import '../view-manager/subscribeUserBasedViewProfiles.js';
import { MainElement } from './main-element.js';
import './session-timeout.js';
import './tailwind.css' with { type: 'tailwind' };
import './translations.js';

import '@adornis/chemistry/elements/components/x-icon.css';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import '@adornis/dialog/dialog-functions.css';
import '@adornis/fonts/fontSrc/lato.css';
// the Adornis typeface the design's `font-sans` names; Lato above stays for the legacy chemistry x-elements
import '@adornis/fonts/fontSrc/inter.css';
import '@adornis/popover/x-dropdown.css';

import { initWiki } from '@adornis/wiki/init.js';
import { setDefaultOptions } from 'date-fns';
import { de } from 'date-fns/locale';
import { initMartinusAuthentication } from '../authentication/martinus-authentication.js';
import { initSessionTimeout } from './session-timeout.js';
import { promptReloadOnVersionMismatch } from './version-check.js';

setDefaultOptions({ locale: de });

const transportHandler = BaseQLClient.createTransportHandler({
  onVersionInfo: promptReloadOnVersionMismatch,
  onReconnect: currentUserReconnectGuard({
    onLost: () => alertDialog('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'),
  }),
});
const queryCache = new QueryCache(transportHandler);
BaseQLClient.initHandler(queryCache);

DesignSystem.setTheme(
  'Martinus',
  DesignSystem.extendBase(
    { fontFamily: 'Lato', fontFamilyHeading: 'Lato', baseFontSize: 18 },
    { neutral: '#919191', tone: { placeholder: '#636363' } },
  ),
);
DesignSystem.useTheme('Martinus');

void subscribeCurrentUser(() => ({
  username: 1,
  email: 1,
  roles: { name: 1, contexts: 1, permissions: 1 },
  kreditorenNr: 1,
  debitorennummer: 1,
  nameAbbreviation: 1,
  sorgeberechtigteKinderIDs: 1,
  spendenKinderIDs: 1,
  createdAt: 1,
  emailConfirmed: 1,
  microsoftUserData: selectionSet(() => MicrosoftUserData, 1),
  mitarbeiterDaten: {
    einstellungsStatus: 1,
    microsoftID: 1,
    anBeschaeftigung: { abrechnungsgruppe: 1 },
    jobTicketKenntnis: 1,
    jobTicketBestaetigt: 1,
    jobTicketDatenweitergabe: 1,
    jobTicketExportiertAm: 1,
    jobTicketBestellDatum: 1,
    jobTicketKuendigungsDatum: 1,
  },
  profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
}));

provideGlobalContext(translationContext, m);
registerMartinusCache();

registerCountryLocale(countryLanguageDe);

initSessionTimeout();

initWiki({});

// the login screen needs the initialized authentication client before the first render
void initMartinusAuthentication().then(() => initApp(() => MainElement()));
